import { FormEvent, useEffect, useState } from 'react'
import type { ModuleMaterial } from '@/lib/materials'
import { saveModuleMaterial, uploadPresentationPdf } from '@/lib/materials'

interface ModuleMaterialFormProps {
  courseId: string
  courseSlug: string
  moduleId: string
  creator: string
  material?: ModuleMaterial
  onSaved: (record: ModuleMaterial) => void
}

function ModuleMaterialForm({
  courseId,
  courseSlug,
  moduleId,
  creator,
  material,
  onSaved,
}: ModuleMaterialFormProps) {
  const [geeUrl, setGeeUrl] = useState(material?.geeUrl ?? '')
  const [zoomUrl, setZoomUrl] = useState(material?.zoomUrl ?? '')
  const [pdfUrl, setPdfUrl] = useState(material?.pdfUrl ?? '')
  const [pdfName, setPdfName] = useState(material?.pdfName ?? '')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    setGeeUrl(material?.geeUrl ?? '')
    setZoomUrl(material?.zoomUrl ?? '')
    setPdfUrl(material?.pdfUrl ?? '')
    setPdfName(material?.pdfName ?? '')
  }, [material?._id, material?.geeUrl, material?.zoomUrl, material?.pdfUrl, material?.pdfName])

  const onUpload = async (file?: File) => {
    if (!file) return
    setError('')
    setMessage('')
    setBusy(true)
    try {
      const uploaded = await uploadPresentationPdf(file)
      setPdfUrl(uploaded.url)
      setPdfName(uploaded.name)
      setMessage('PDF subido. Guarda para publicarlo en el aula.')
    } catch (err) {
      console.error('No se pudo subir el PDF:', err)
      setError(err instanceof Error ? err.message : 'No se pudo subir el PDF.')
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setMessage('')
    setBusy(true)
    try {
      const record = await saveModuleMaterial({
        existingId: material?._id,
        courseId,
        courseSlug,
        moduleId,
        pdfUrl,
        pdfName,
        geeUrl,
        zoomUrl,
        creator,
      })
      onSaved(record)
      setMessage('Materiales del módulo guardados.')
    } catch (err) {
      console.error('No se pudieron guardar los materiales:', err)
      setError('No se pudieron guardar. Comprueba que tu cuenta es administradora.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 space-y-3 border-t border-[#1E3A5F]/10 pt-4">
      <p className="text-xs uppercase tracking-[0.18em] text-[#C41E3A]">Materiales (administración)</p>
      <label className="block text-xs">
        PDF de la presentación
        <input
          type="file"
          accept="application/pdf,.pdf"
          onChange={(event) => void onUpload(event.target.files?.[0])}
          className="mt-1 block w-full text-sm file:mr-3 file:border file:border-[#1E3A5F]/20 file:bg-transparent file:px-3 file:py-1.5"
        />
      </label>
      {pdfName ? (
        <p className="text-xs text-[#1E3A5F]/60">Archivo listo para visualizar: {pdfName}</p>
      ) : null}
      <label className="block text-xs">
        Liga de códigos GEE
        <input
          type="url"
          value={geeUrl}
          onChange={(event) => setGeeUrl(event.target.value)}
          placeholder="https://code.earthengine.google.com/…"
          className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-xs">
        Enlace de Zoom (si existe)
        <input
          type="url"
          value={zoomUrl}
          onChange={(event) => setZoomUrl(event.target.value)}
          placeholder="https://zoom.us/j/…"
          className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
        />
      </label>
      {error ? <p className="text-xs text-[#C41E3A]">{error}</p> : null}
      {message ? <p className="text-xs text-[#1E3A5F]/65">{message}</p> : null}
      <button
        type="submit"
        disabled={busy}
        className="bg-[#1E3A5F] px-3 py-2 text-sm text-[#F4EFE6] disabled:opacity-50"
      >
        {busy ? 'Guardando…' : 'Guardar materiales'}
      </button>
    </form>
  )
}

export default ModuleMaterialForm
